import React from 'react';

import { CodeComparison } from '../CodeComparison.tsx';
import { makeConnectRequest, makeExecuteRequest, Message_t, sendExecuteRequest, SocketState_t } from '../Socket.tsx';
import { Footnote, FootnoteRef } from '../FootNote.tsx';
import { CodeSection_t, Editor } from '../Editor.tsx';
import { deadSpace, logo, pageStrawMan, slotArray, rle, dictionaryEncoding, deltaEncoding, bitPacking, mostlyEncoding } from '../Static.tsx';
import { helloWorldBlankFile, helloWorldSolutionFile, storageBlankFile, storageSolutionFile } from '../RustCode.tsx';
import { EditorState } from "@codemirror/state"
import { defaultKeymap, historyKeymap, history } from "@codemirror/commands"
import { drawSelection, keymap, lineNumbers } from "@codemirror/view"
import { undo, redo } from "@codemirror/commands"
import { EditorView } from "@codemirror/view"
import { Transaction, Annotation } from "@codemirror/state"
import { rust } from '@codemirror/lang-rust';
import { oneDark } from '@uiw/react-codemirror';
import { useFileContents } from '../Util.tsx';
import { Comments } from '../Comments.tsx';

const HomeButton: React.FC<{}> = () => {
  return (<div className='clicky' onClick={() => (document.location.href = '#')}>
    <div className="logo-container" style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src={logo}
        alt="Logo"
        className="startup-logo"
        width={"80px"}
      />
      <h2 style={{ margin: 0, marginLeft: "10px" }}>Playground DB </h2>
    </div>
  </div>);
}

const NUM_CODE_REFS = 14;

export const Part1: React.FC<{}> = () => {
  const blank = useFileContents(storageBlankFile);
  const solution = useFileContents(storageSolutionFile);

  const helloWorldSolution = useFileContents(helloWorldSolutionFile);
  const helloWorldBlank = useFileContents(helloWorldBlankFile);

  const helloWorldEditorRef = React.useRef(null);
  const helloWorldView = React.useRef<EditorView | null>(null);

  const fixedHeightEditor = React.useMemo(() => EditorView.theme({
    "&": {
      height: "100%",
    },

    // these next parts hide the scroll bar
    ".cm-scroller": {
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    },
    ".cm-scroller::-webkit-scrollbar": {
      display: "none",
    }
  }), []);

  const extensions = React.useMemo(() => [
    rust(),
    history(),
    drawSelection(),
    lineNumbers(),
    fixedHeightEditor,
    oneDark,
    keymap.of([
      ...defaultKeymap,
      ...historyKeymap,
    ])
  ], []);

  React.useEffect(() => {
    if (helloWorldBlank) {
      const state = EditorState.create({
        doc: helloWorldBlank,
        extensions,
      });
      const view = new EditorView({
        state,
        parent: helloWorldEditorRef.current ?? undefined,
      });
      helloWorldView.current = view;
    }
  }, [helloWorldBlank]);

  const mainEditorRef = React.useRef(null);
  const mainViewRef = React.useRef<EditorView | null>(null);

  // NOTE this is not the cleanest thing but we need to sync multiple editors. I found the best UX
  // was to have 1 text file that is edited by multiple editors. It's as if you open the same file 
  // in multiple tabs in your code editor. The example comes from https://codemirror.net/examples/split
  // I have one main view and multiple synced ones. These are in an array and I have to manually index into it
  // correctly when I add a new code section. There is probably a better way by putting these in the map 
  const syncedEditorRefs = React.useRef<React.RefObject<HTMLDivElement | null>[]>([]);
  React.useEffect(() => {
    if (syncedEditorRefs.current.length === 0) {
      for (let i = 0; i < NUM_CODE_REFS; i++) {
        syncedEditorRefs.current.push(React.createRef());
      }
    }
  }, []);

  const syncedViewsRefs = React.useRef<(EditorView | null)[]>(new Array(NUM_CODE_REFS).fill(null));

  const [viewIsReady, setViewIsReady] = React.useState<boolean>(false);

  const blankIsNull = blank === '';

  // TODO hacky. Ideally this could be modularized and contained within a class that handles the editors.
  React.useEffect(() => {
    if (!blankIsNull) {

      let startState = EditorState.create({ doc: blank, extensions });

      const makeOtherState = () => {
        return EditorState.create({
          doc: startState.doc,
          extensions: [
            ...extensions,
            keymap.of([
              ...defaultKeymap,
              { key: "Mod-z", run: () => undo(mainView) },
              { key: "Mod-y", mac: "Mod-Shift-z", run: () => redo(mainView) }
            ])
          ]
        });
      }
      let otherState = makeOtherState();

      let syncAnnotation = Annotation.define<boolean>();

      function syncDispatch(tr: Transaction, view: EditorView, others: EditorView[]) {
        view.update([tr])
        if (!tr.changes.empty && !tr.annotation(syncAnnotation)) {
          let annotations: Annotation<any>[] = [syncAnnotation.of(true)]
          let userEvent = tr.annotation(Transaction.userEvent)
          if (userEvent) annotations.push(Transaction.userEvent.of(userEvent))
          for (const o of others) {
            o.dispatch({ changes: tr.changes, annotations });
          }
        }
      }

      let mainView = new EditorView({
        state: startState,
        parent: mainEditorRef.current ?? undefined,
        dispatch: tr => syncDispatch(tr, mainView, otherViews)
      });

      const makeOtherView = (i: number) => {
        const view = new EditorView({
          state: otherState,
          parent: syncedEditorRefs.current[i].current ?? undefined,
          dispatch: tr => syncDispatch(tr, otherViews[i], [...otherViews.filter((_, index) => index !== i), mainView]),
        });
        return view;
      }

      const otherViews: EditorView[] = [];

      for (let i = 0; i < NUM_CODE_REFS; i++) {
        otherViews.push(makeOtherView(i));
      }

      mainViewRef.current = mainView;
      for (let i = 0; i < NUM_CODE_REFS; i++) {
        syncedViewsRefs.current[i] = otherViews[i];
      }

      setViewIsReady(true);
    }
  }, [blankIsNull]);

  const setDoc = React.useCallback((doc: string) => {
    if (mainViewRef.current) {
      mainViewRef.current.dispatch({
        changes: {
          from: 0,
          to: mainViewRef.current.state.doc.length,
          insert: doc,
        }
      });
    } else {
      console.log("Trying to set doc when editor is null");
    }
  }, []);

  const getDoc = React.useCallback(() => {
    if (mainViewRef.current) {
      return mainViewRef.current.state.doc.toString();
    } else {
      return '';
    }
  }, []);

  const setDocHelloWorld = React.useCallback((doc: string) => {
    if (helloWorldView.current) {
      helloWorldView.current.dispatch({
        changes: {
          from: 0,
          to: helloWorldView.current.state.doc.length,
          insert: doc,
        }
      });
    } else {
      console.log("Trying to set doc when editor is null");
    }
  }, []);

  const getDocHelloWorld = React.useCallback(() => {
    if (helloWorldView.current) {
      return helloWorldView.current.state.doc.toString();
    } else {
      return '';
    }
  }, []);

  const solutionBlocks: Record<string, string> = React.useMemo(() => {
    let solutionBlocks: Record<string, string> = {};

    let existing: string = '';
    let prev_key: string = '';
    solution.split('\n').forEach((line, index) => {
      if (line.startsWith("// ===")) {
        if (prev_key) {
          solutionBlocks[prev_key] = existing;
          existing = '';
        }

        prev_key = line;
      }

      existing += line + '\n';
    });

    solutionBlocks[prev_key] = existing;

    return solutionBlocks;
  }, [solution]);

  const codeBlocks: Record<string, CodeSection_t> = React.useMemo(() => {
    const codeBlocks: Record<string, CodeSection_t> = {};

    let i = 0;
    for (const key in solutionBlocks) {
      codeBlocks[key] = {
        name: key,
        blank: blank,
        solution: solutionBlocks[key],
        refIndex: i,
      };
      i++;
    }

    // TODO HACKY
    // I'd like to know the number of code blocks ahead of time and set the editor ref programmatically but
    // my attempt at doing so seemed to prevent the non main editor from rendering.
    // A lot of this page already has to be constructed manually so it isn't a big deal.
    if (i >= NUM_CODE_REFS) {
      throw Error('you should have more editor references. NUM_CODE_REFS should be >= than the number of code blocks ')
    }

    return codeBlocks;
  }, [solutionBlocks, blank]);

  const [socketState, setSocketState] = React.useState<SocketState_t>({ sequenceNumber: 0, messages: [], editorNameToSeqNumber: {} });


  function generateRandomString(n: number): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < n; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const socket = React.useMemo(() => {
    const socket = new WebSocket("wss://play.rust-lang.org/websocket");
    socket.addEventListener("message", (event) => {
      const message: Message_t = JSON.parse(event.data);
      setSocketState(state => ({
        ...state,
        sequenceNumber: message.meta.sequenceNumber,
        messages: [...state.messages, message],
      }))
    });

    socket.addEventListener("open", (event) => {
      socket.send(JSON.stringify(makeConnectRequest(socketState.sequenceNumber)));
      //sendExecuteRequest(socket, 1, `fn main() { println!("hi"); let s = String::from("${generateRandomString(1_000_000)}");}`, false);
    });

    return socket;
  }, []);

  return (
    <div className="App" style={{ paddingBottom: '32px' }}>
      <HomeButton />
      <header className='App-header'>
        <h1>Playground DB</h1>
        <h4>Build a SQL database in the Rust Playground</h4>
        <div style={{ padding: "5px", color: 'black', fontSize: 15, textAlign: 'left' }}>
          <h3>
            Neil Lugovoy - <a style={{ fontWeight: 'bold' }} href="https://truesearcher.com">True Searcher</a>
          </h3>
        </div>
      </header>
      <div
        style={{
          textAlign: 'left',
          fontSize: '1.2em',           // base font size
          background: "#f5f5f5",
          color: 'black',
          borderRadius: "5px",
          border: "1px solid black",   // fixed border syntax
          width: '75%',
          padding: '16px',
        }}
      >
        <a href={`#part-1`}
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById(String('part-1'));
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
        >
          Skip all the talking and jump to coding part 1!
        </a>

        <h2>Intro</h2>
        <div className='selector-of-that-element'>
          <p>
            Databases are ubiquitous.
          </p>
          <p>
            If you work on mobile or desktop development, Apple's Core Data storage API defaults to SQLite <Footnote refNumber={1} /> and 1st party applications like Photos and
            Safari use SQLite <Footnote refNumber={2} /> <Footnote refNumber={3} />. Android also offers a SQLite <Footnote refNumber={4} /> API for developers and uses SQLite internally for Contacts, Chrome, Gmail etc. <Footnote refNumber={5} />.
          </p>
          <p>
            If you work on embedded systems, SQLite is used in the flight software for the Airbus A350 XWB family of aircraft <Footnote refNumber={6} />, in Boeing's Onboard Performance Tool in 787 &
            777s<Footnote refNumber={7} /> <Footnote refNumber={8} />, in GE appliances<Footnote refNumber={6} /> and in Bosch's automotive systems <Footnote refNumber={6} />. SQLite has 100% MC/DC test coverage <Footnote refNumber={9} /> which is required for the
            DO-178B standard for safety-critical software <Footnote refNumber={10} /> for use in embedded systems.
          </p>
          <p>
            If you work on a web applications, you probably had to do a system design interview with the classic <code>client {'<'}-{'>'} server {'<'}-{'>'} database</code> architecture just to get the job. Every time the user logs in,
            the password they type in is compared against their (salted and hashed hopefully! <Footnote refNumber={11} />) password stored in a database. Every comment, message, like, subscription, document, photo, and video requires a write to
            a database. Even on a static WordPress site, a single pageload can trigger dozens of SQL queries. <Footnote refNumber={12} />.
          </p>
          <p>
            If you work on AI, you likely use a key value database to cache outputs and intermediate results <Footnote refNumber={13} />.
          </p>
          <p>
            If you care about your web application's performance, you almost definitely have a DBA and know that a single poorly-formed query
            (e.g. <code>SELECT * FROM your_table FOR UPDATE;</code>) can result in poor performance for all users. Although adding more pods of your stateless microservice is easy, scaling up your
            database is not so straightforward.
          </p>
          <p>
            If you care about correctness, you may start to wonder what happens if multiple requests edit a user's password and then they try to log in? Can you see partial results from another ongoing transaction?
            What does <code>READ COMMITTED vs REPEATABLE READ</code> <Footnote refNumber={14} /> mean for my application code? Do I need to explicitly grab a lock in every query?
          </p>
          <p>
            If you care about fault tolerance, you may wonder what happens if the database crashes when your request is modifying the database? If your database holds the bank balances of customers or the coefficients of the PID controller
            <Footnote refNumber={15} /> for your airplane and the data is corrupted, that could be a big bummer. Even if your database just holds a user's favorite frog facts, it still would be a sizeable bummer.
          </p>
          <h3>Questions we will answer</h3>
          <ol>
            <li>
              If I have a big column that I rarely use, does that slow down queries on that table that don't use that column?
            </li>
            <li>
              Disks can only do 1000 <code>fsync</code> / second. How do durable databases like MySQL and
              PostgreSQL achieve 5000 transactions / second?
            </li>
            <li>
              Will storing too much data in a database slow it down? Why? Do # of rows or # of bytes matter more?
            </li>
            <li>
              Does having multiple indexes on a table slow down inserts? How much
              and in what scenarios?
            </li>
            <li>
              Do filters on columns that don't have an index always result in a full
              table scan?
            </li>
            <li>
              How does my database pick the most efficient query plan for my
              query? Can I give it hints?
            </li>
            <li>
              If multiple transactions are reading/writing data simultaneously,
              how do they interact? What do transaction isolation levels do? When do I need
              to explicitly grab a lock in my query?
            </li>
            <li>
              I know my database is durable but if my database crashes, how long before it can serve requests
              again?
            </li>
          </ol>

          <h3>Why should you learn about databases?</h3>

          <ol>
            <li>
              You are extremely likely to use one. Learning how to use it properly
              will make your job easier.
            </li>
            <li>
              Databases have made progress on many hard problems: caching, performance optimization tricks, compression, fault tolerance, distributed systems and more.
              If you have a problem, check if someone from databases has worked on it!
            </li>
            <li>Drama + lore! There is a surprising amount of this in databases.</li>
          </ol>

          <h3>Why is learning databases hard?</h3>

          <ol>
            <li>Few engineers will ever actually build a database.</li>
            <li>
              Databases are not very modular which makes getting hands on
              experience even more difficult.

              You can't take the query optimizer from MySQL and swap it out with SQLite's optimizer.

              Additionally some subsystems in a DB will affect others. For example your crash recovery approach (likely a write-ahead log) will affect your disk scheduler and buffer pool. Your concurrency control approach (likely multi-version concurrency control) will affect your indexes and query execution. If you don't know how your whole system will work from day 1, you might have to re-write a lot of code.
            </li>
            <li>
              Database literature is notoriously difficult to understand and papers rarely come with working code. For example the Cascades Optimizer paper was published in
              1995 <Footnote refNumber={17} /> but database Professor Andy Pavlo said “there really wasn’t a complete description of what Cascades actually is and how to implement
              it prior to [a 2024 book on query optimizers from Microsoft]" <Footnote refNumber={18} /> <Footnote refNumber={19} />
            </li>
          </ol>

          <p>
            When reading these papers about top-down vs bottom-up optimization or
            push vs pull execution I just wanted to see some code! Or even better,
            write some code!
          </p>

          <p>
            So I’ve made this interactive guide. Everything runs in the Rust
            Playground and it is completely modular so you can choose to work on
            any component. If you've always wanted to write a database execution engine,
            jump to that section!
          </p>

          <Editor
            socket={socket}
            socketState={socketState}
            setSocketState={setSocketState}
            isTest={false}
            codeSection={{
              name: '// === Intro ===',
              blank: helloWorldBlank,
              solution: helloWorldSolution,
              refIndex: -1,
            }}
            ref={helloWorldEditorRef}
            scrollOffset={0}
            setDoc={setDocHelloWorld}
            getDoc={getDocHelloWorld}
            viewIsReady={true}
          />
          <p>I've made these sections modular through Rust's traits which are a bit like interfaces in Java + Go + TypeScript or abstract base classes in Python. <Footnote refNumber={37} /></p>
          <p>As long as your code implements the trait and passes the tests it will work!</p>
          <p>All of your code is saved in local storage.</p>
          <p>All of the editors on a page (with the exception of the hello world one above) look at the same code. So if you need to change your implementation from a previous part, you can just scroll up in the editor.</p>
          <p>If you don’t want to code and just want to follow along that’s cool too!</p>

          <h3>Sections</h3>

          <ol>
            <li>
              <a href={"#/1"}>
                Part 1 - Serialization
              </a>
            </li>
            <li>
              Part 2 - Disk Layout & Scheduler
            </li>
            <li>
              Part 3 - Buffer Pool
            </li>
            <li>
              Part 4 - Concurrent B+ Tree Index
            </li>
            <li>
              Part 5 - Query Execution Engine
            </li>
            <li>
              Part 6 - Query Optimizer
            </li>
            <li>
              Part 7 - Transactions - Multi Version Concurrency Control
            </li>
            <li>
              Part 8 - Transactions - Fault Tolerance & Recovery
            </li>
          </ol>

          <p>I’ll also try to teach you some Rust along the way.</p>

          <h3>Background: Why is SQL so weird?</h3>

          <p>
            SQL may feel unwieldy, which is in part due to it being declarative. In a declarative language you specify the output you want but not how the output is constructed.
            (You might be more accustomed to an imperative language such as Python, Java, Go etc.) This is by design!
          </p>

          <p>
            In 1970 E.F. Codd proposed the Relational Model <Footnote refNumber={20} /> where application programmers would just specify the output they wanted and it would leave the database free to represent
            and compute the output however it liked. This "provides a basis for a high level data language which will yield maximal independence between programs on the one hand and machine representation and organization of data on
            the other". Contrast this manipulating the data manually in Python:
          </p>

          <CodeComparison />

          <p>There's more problems you may have to deal with. What if users are modifying those tables concurrently? What if a write crashes part way through?</p>
          <p>Databases solve these hard problems for you, so that you can focus on perfecting your app/autopilot/washing machine.</p>

          <p>In many ways the relational model is extremely flexible.</p>

          <p>
            <code>select * from table limit 10;</code>
          </p>

          <p>
            does not specify the order the rows are returned or where or how the data is stored. If the table has more than 10 rows
            the set of rows can be different every time and it would still be correct.
          </p>

          <p>
            <code>SELECT * FROM employees e JOIN stores s ON e.store_id = s.id;</code>
          </p>

          <p>
            does not specify how we join. Perhaps years later a brilliant database researcher joins our team and comes up with a more efficient approach.
            We can implement her optimization without requiring our customers to change their SQL code.
          </p>

          <p>
            This leads to another reason that SQL is funky. Because SQL is so old and popular, it is very difficult to dislodge it with a new query language that is easier to use. Many of the NoSQL
            databases now support SQL or SQL-like query languages to offer wider compatibility. MongoDB<Footnote refNumber={21} />, Cassandra<Footnote refNumber={22} />, BigTable<Footnote refNumber={23} />,
            Couchbase<Footnote refNumber={24} />, and DynamoDB<Footnote refNumber={25} /> to name a few.
          </p>
        </div>
        <h2 id='part-1'>Part 1 - Serialization</h2>
        <h4>Part 1.1 Schema</h4>
        <p>First we need to represent a schema. Correctness is super important in databases. I would recommend you include a lot of checks for incorect states at runtime (e.g. exactly 1 primary key).
          You can also try to make incorrect states unrepresentable by enforcing them at compile time. For example a non-unique primary key is impossible since they are different enum variants. For fun can you think
          of how you could change the Schema definition so that we always have exactly one PrimaryKey column?</p>
        {codeBlocks['// === Part 1.1 Schema ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.1 Schema ===']}
          getDoc={getDoc}
          ref={mainEditorRef}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={0}
        />}
        <h4>Part 1.2 Schema Serialization</h4>
        <p>To store our tables on disk we need to turn our Schema into bytes and vice-versa. If everything in your struct is a constant length it's easy! But if you have variable length fields
          like strings and vectors then you need to store the length somewhere. One of the most common approaches is length-prefix encoding where the length of the value is stored before the value.</p>
        <p>For example we store the string "Hello" as <code>serialize(5)|serialize(Hello)</code>. In bytes it would be <code>00 05 | 48 65 6C 6C 6F</code> assuming we are using <code>u16</code> for our length.</p>
        <p>Redis<Footnote refNumber={26} /> and Protobufs<Footnote refNumber={27} /> both use this approach.</p>
        <p>To know which value corresponds to which field you can always serialize fields in the same order e.g. <code>serialize(value for key 'foo')|serialize(value for key 'bar')</code> or
          include the key with the value <code>serialize('foo')|serialize(value for key 'foo')|serialize('bar')|serialize(value for key 'bar')</code>. Protobufs use the latter approach so that
          key value pairs can serialized in any order and to ensure backwards compatibility, this is why you can't remove old fields when you update a Protobuf definition.</p>
        {codeBlocks['// === Part 1.2 Schema Serialization ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.2 Schema Serialization ===']}
          getDoc={getDoc}
          ref={syncedEditorRefs.current[0]}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={30}
        />}
        <h4>Part 1.3 Row Serialization</h4>
        <p>Now we do the same for rows. You'll have to handle null values here. There's a few approaches
          here like including a null bit before the value or adding a header to the beginning of the row with a nullmap.</p>
        {codeBlocks['// === Part 1.3 Row Serialization ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.3 Row Serialization ===']}
          getDoc={getDoc}
          ref={syncedEditorRefs.current[1]}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={40}
        />}
        <h4>Part 1.4 Pages</h4>
        <p>Databases divide their data into fixed-length blocks of bytes called pages.
          Every disk will have a maximum page size that it can write atomically to disk; this will matter for fault tolerance.
          Obviously the database page size cannot be larger than the disk's page size.
          To give a sense of scale the default MySQL page size is 16KB, PostgreSQL is 8KB, and SQLite is 4KB.
        </p>
        <p>
          We are going to use a row-store where each row is stored contiguously within a slot in a page. Our index will map from primary key values
          to <code>(page_number, slot_number)</code> tuples so we know where to look up the row.
        </p>
        <p>
          Now how do we pack the rows into the pages? Let’s just start with the simplest thing: making our page one big array of rows.
        </p>
        <img src={pageStrawMan} alt="page straw man" height={'400px'} />
        <p>
          One problem is that since rows are variable size, we will need to scan through the entire page to find a particular row.
          Also we don’t know where the rows end and the free space begins.
        </p>
        <img src={slotArray} alt="Slot array" height={'400px'} />
        <p>Instead we will use a slot array with slots that point to where in the page the row is stored and its length (length not pictured in diagram for simplicity). We can also maintain a
          header that tells us where the start of the free space is and the number of rows. Now looking up a row by its slot number is constant time.</p>
        {codeBlocks['// === Part 1.4 Pages ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.4 Pages ===']}
          getDoc={getDoc}
          ref={syncedEditorRefs.current[2]}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={60}
        />}
        <h4>Part 1.5 Pages Delete and Update</h4>
        <h4><i>How do we delete?</i></h4>
        <p>
          Note that when we delete a row from our page, if we have a separate index, the page could not change and still be correct. The important part is deleting the entry from the index that maps from
          primary key value to <code>(page_number, slot_number)</code>. That index will be serialized too and we'll see that later.
        </p>
        <p>
          If we don't have a separate index then we will have to update our page.
        </p>
        <p>
          There's a little more subtlety here about whether our index stores the rows themselves in the index or just a pointer to the row in the form of (page_number, slot_number) and this is the difference between heap file tuple storage and index oriented storage.
        </p>
        <img src={deadSpace} alt="fragmentation" height={'400px'} />
        <p>Updates that resize a row or deletes might cause our page to be fragmented. We could clean up our page on every delete and update but that could slow them down.</p>
        <p>Welcome to our first tradeoff. I think it's important to be as precise as possible about the tradeoff. We could just group this with a common broad tradeoff like read vs write but let's think a little more.</p>
        <p>What would be the worst case for each implementation?</p>
        <p>If we didn't compact at all, then we could have a page with only one row in it and the rest is dead space. We would constantly have to fetch pages from disk to perform even the most modest of queries. A page with <i>all</i> dead space would eventually be evicted from our cache because it would never be used but without compaction it would take up space on disk.</p>
        <p>Imagine instead if we compacted on every delete. The worst case would be a series of deletes that always deleted the row at the end of the page and each delete was followed by an insert. If we had a page with n rows and then every delete would have to move n rows over. </p>
        <p>Also ask <i>what</i> are you trading off? Speed? Of which operations? Memory usage? Disk usage? Predictable performance? What matters to you?</p>
        <p>
          You can also think of intermediate solutions, perhaps on update/delete we look for dead space where a new row could go rather than using the free space. Perhaps on every m updates/deletes we compact. Perhaps we have a background
          job that looks for fragmented pages and compacts them. Postgres chooses that option<Footnote refNumber={28} /> and also allows compaction to be triggered manually with the <code>vacuum</code> command<Footnote refNumber={29} />.
        </p>
        <p>
          MySQL uses a free list within a page but can optimize across pages with <code>OPTIMIZE TABLE</code><Footnote refNumber={38} />.
        </p>
        <p>
          I've already implemented the <code>is_deleted()</code> function for your earlier which checks if the top bit of the slot is 1 to see if it is deleted. You're welcome to use this approach or your own.
        </p>
        {codeBlocks['// === Part 1.5 Pages Delete and Update ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.5 Pages Delete and Update ===']}
          getDoc={getDoc}
          ref={syncedEditorRefs.current[3]}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={120}
        />}
        <h4><i>Why not use JSON?</i></h4>
        <p>JSON cannot be incrementally deserialized so we would have to deserialize all of the rows to read or write one of them. Additionally JSON is not a very efficient serialization
          format because of all the braces, colons, quotes, and key names. We will see later that we can use compression techniques to cram more rows into our pages without losing the ability to query them.</p>
        <h4>1.6 Table Serialization & Formatting</h4>
        <p>Now let's put it all together and serialize a table into a bunch of pages. These pages can be serialized into a single file on disk easily and we will do that in the next section.</p>
        <p>I've also tasked you with implementing the <code>Display</code> trait for the <code>Table</code> so we can print it out nice and pretty.</p>
        {codeBlocks['// === Part 1.6 Table Serialization & Formatting ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.6 Table Serialization & Formatting ===']}
          getDoc={getDoc}
          ref={syncedEditorRefs.current[4]}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={150}
        />}
        <h4>1.Optional Columnar Storage & Compression</h4>
        <p>You may notice that I made our slot offset and length <code>u16</code> integers but if our page is 4096 bytes, we could get away with much fewer bits. I also stored whether a value was null in an entire byte rather than one bit. You may also notice that the offset can never be smaller than the size of the header plus the size of a single slot, so we could probably use a few less bits and add that minimum offset. </p>
        <p>If you thought about these things, then you are starting to think about compression. We can use little tricks to store that metadata more efficiently, but we will get massive gains by compressing the values themselves.</p>
        <p>Let's imagine we have only one column and do a quick tour of compression techniques. <Footnote refNumber={16} /></p>
        <h5>Run-length encoding</h5>
        <img src={rle} alt="Run length encoding" height={'400px'} />
        <p>If we have 100 <code>"cat"</code>s in a row, rather than storing <code>"cat"</code> 100 times, we could store that we have 100 <code>"cat"s</code></p>
        <h5>Dictionary encoding</h5>
        <img src={dictionaryEncoding} alt="Dictionary encoding" height={'400px'} />
        <p>If we only have 4 unique values, we only need ceil(log_2(4)) = 2 bits to store each value along with a mapping from bitstring to value. Many of the generic compression algorithms (gzip etc.) build off this idea. </p>
        <h5>Delta encoding</h5>
        <img src={deltaEncoding} alt="Delta encoding" height={'400px'} />
        <p>If our data doesn't change much between samples then we could simply store the delta between subsequent values. Timeseries data, timestamps, sequential IDs, stock prices etc. would all work well for this.</p>
        <h5>Bit packing</h5>
        <img src={bitPacking} alt="Bit packing" height={'400px'} />
        <p>If our data is stored in a <code>u32</code> but the values are all less than 26 we only need ceil(log_2(26)) = 5 bits to represent each and we can tack on the leading 0s when we return them to the client.</p>
        <p>If our data is in a small range that doesn't start at 0 (e.g. 1000 to 5000) then we only need ceil(log_2(5000 - 1000)) = 14 bits and we can just add 1000 to get the true value.</p>
        <h5>Mostly encoding</h5>
        <img src={mostlyEncoding} alt="Mostly encoding" height={'400px'} />
        <p>If our data is stored in a <code>u32</code> but most of the values are less than 5000 we can store the other large values separately and use bit packing for the rest.</p>
        <h5>Combine them!</h5>
        <p>If we use a delta encoding, our deltas will probably be pretty small so we can use bit packing. Also it's likely we have runs of the same delta so we can use RLE.</p>
        <h4><i>What about when we have a query? Do we have to decompress?</i></h4>
        <p>If I say upper case all the values in this run-length encoded column <code>[(100, "cat"), (200, "dog"), (5, "cat")]</code>, are you going to expand it to 305 values to upper case each one?</p>
        <p>If you are familar with gzip and friends you might be used to the idea that compressed data is an opaque blob that you can't operate on until it is decompressed.</p>
        <p>But if your compression scheme and operators are aware of each other, you can perform some operations on compressed data. For example tries compress strings that share the same prefix and also support prefix matching queries without uncompressing the data. <Footnote refNumber={30} /> <Footnote refNumber={31} /></p>
        <p>Finite-state transducers compress prefixes and suffixes and allow efficient edit distance queries that aren't possible on the uncompressed data! <Footnote refNumber={32} /></p>
        <p>
          We might not be able to support every type of query on the compressed data, so it can be helpful to know
          ahead of time what the common operations are.
          With dictionary encoding if we keep the bitstrings in the same order as the original data, then we can do
          comparison operations. But this requires we know all of the values at the time we compress and an insert
          with a new value may require re-compressing all of the data.
        </p>
        <h3>Columnar storage</h3>
        <p>In the previous section on compression we assumed that our table had one column. Up to this point we have been storing our data as a collection of rows.
          If our table has multiple columns it will be very difficult or impossible to use a row-oriented architecture with some of these compression techniques such as run-length encoding.</p>
        <p>Also notice that even if we are querying a single column, we still have to fetch the entire row.</p>
        <p>An alternative approach is to store the columns contiguously in a columnar format. <Footnote refNumber={33} /> <Footnote refNumber={34} /> </p>
        <p>For queries on massive amounts of data, columnar storage has many benefits. Due to branch-prediction, pre-fetching, auto-vectorization etc. CPUs can perform the same operation
          on a run of contiguous data much faster than they can do those operations if there a lot of indirection in between.</p>
        <p>A paper comparing row-store to column-store databases mentioned how even just the overhead to fetch a tuple may be more than the operation (+, = etc.) being performed "in many cases,
          this leads to tuple-at-a-time processing, where
          there are 1-2 function calls to extract needed data from a tuple for
          each operation (which if it is a small expression or predicate evaluation is low cost compared with the function calls)" <Footnote refNumber={35} /> </p>
        <p>Of course columnar storage introduces new challenges. To get a single row, we have to piece it together from multiple column-store pages. Making a small update is much more complicated
          than in row-store and may require uncompressing many pages of data.</p>
        <p>Also all of our columns must be stored in the same order but the order greatly influences the effectiveness of our compression scheme. Picking the optimal order is NP hard. <Footnote refNumber={36} /> </p>
        <p>I've built a test harness that shows your compression ratio.</p>
        {codeBlocks['// === Part 1.Optional Columnar Storage & Compression ==='] && <Editor
          socket={socket}
          socketState={socketState}
          isTest={true}
          codeSection={codeBlocks['// === Part 1.Optional Columnar Storage & Compression ===']}
          getDoc={getDoc}
          ref={syncedEditorRefs.current[5]}
          setDoc={setDoc}
          setSocketState={setSocketState}
          viewIsReady={viewIsReady}
          scrollOffset={150}
        />}
        <p>Thanks for checking this out. I'll see you all in the next section next month!</p>
        <section id="footnotes">
          {/* TODO these footnotes are a little hacky. The number in front of them comes from the ol (ordered list) but you have to manually 
              keep the ref numbers in order and contiguous. */}
          <ol>
            <FootnoteRef refNumber={1} ref={"https://stackoverflow.com/a/55626085"} />
            <FootnoteRef refNumber={2} ref={"https://simonwillison.net/2020/May/21/dogsheep-photos/"} />
            <FootnoteRef refNumber={3} ref={"https://apple.stackexchange.com/questions/358639/does-safari-browser-history-db-contain-transition-type"} />
            <FootnoteRef refNumber={4} ref={"https://developer.android.com/training/data-storage/sqlite"} />
            <FootnoteRef refNumber={5} text='Page 2 Table 2-appdata ' ref={"https://newandroidbook.com/Book/2-Excerpt-Data.pdf"} />
            <FootnoteRef refNumber={6} ref={"https://sqlite.org/famous.html"} />
            <FootnoteRef refNumber={7} ref={"https://www.pentestpartners.com/security-blog/database-integrity-vulnerabilities-in-boeings-onboard-performance-tool/"} />
            <FootnoteRef refNumber={8} ref={"https://services.boeing.com/flight-operations/flight-performance/onboard-performance-tool"} />
            <FootnoteRef refNumber={9} ref={"https://sqlite.org/testing.html"} />
            <FootnoteRef refNumber={10} text='at 18:15 ' ref={"https://corecursive.com/066-sqlite-with-richard-hipp"} />
            <FootnoteRef refNumber={11} ref={"https://textbook.cs161.org/crypto/passwords.html#148-password-hashing-done-right"} />
            <FootnoteRef refNumber={12} ref={"https://martech.zone/wordpress-troubleshooting-and-optimizing-your-template-sql-queries-with-savequeries"} />
            <FootnoteRef refNumber={13} ref={"https://aws.amazon.com/blogs/machine-learning/managed-tiered-kv-cache-and-intelligent-routing-for-amazon-sagemaker-hyperpod/"} />
            <FootnoteRef refNumber={14} ref={"https://www.postgresql.org/docs/current/transaction-iso.html#TRANSACTION-ISO"} />
            <FootnoteRef refNumber={15} ref={"https://en.wikipedia.org/wiki/Proportional%E2%80%93integral%E2%80%93derivative_controller#Control_function"} />
            <FootnoteRef refNumber={16} text='at 1:02:01 ' ref={"https://youtu.be/yWnToWrskXE?si=Oi6dKHrcFVIxW6uH&t=3721"} />
            <FootnoteRef refNumber={17} ref={"https://15721.courses.cs.cmu.edu/spring2016/papers/graefe-ieee1995.pdf"} />
            <FootnoteRef refNumber={18} text='at 00:38 ' ref={"https://www.youtube.com/watch?v=bjOIbEg0Stc&t=38s"} />
            <FootnoteRef refNumber={19} ref={"https://www.microsoft.com/en-us/research/publication/extensible-query-optimizers-in-practice/"} />
            <FootnoteRef refNumber={20} ref={"https://www.seas.upenn.edu/~zives/03f/cis550/codd.pdf"} />
            <FootnoteRef refNumber={21} ref={"https://www.mongodb.com/products/platform/atlas-sql-interface"} />
            <FootnoteRef refNumber={22} ref={"https://cassandra.apache.org/doc/4.0/cassandra/cql/"} />
            <FootnoteRef refNumber={23} ref={"https://docs.cloud.google.com/bigtable/docs/googlesql-overview"} />
            <FootnoteRef refNumber={24} ref={"https://query-tutorial.couchbase.com/tutorial/#1"} />
            <FootnoteRef refNumber={25} ref={"https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/ql-reference.html"} />
            <FootnoteRef refNumber={26} ref={"https://redis.io/docs/latest/develop/reference/protocol-spec/#bulk-strings"} />
            <FootnoteRef refNumber={27} ref={"https://protobuf.dev/programming-guides/encoding/#length-types"} />
            <FootnoteRef refNumber={28} ref={"https://www.postgresql.org/docs/current/routine-vacuuming.html"} />
            <FootnoteRef refNumber={29} ref={"https://www.postgresql.org/docs/current/sql-vacuum.html"} />
            <FootnoteRef refNumber={30} ref={"https://docs.google.com/presentation/d/1ZDobmVNNZIayrgDUZzZVM-t7yr6ZZCkWBhC6aEjtC04/edit?slide=id.g46b429e30_0369#slide=id.g46b429e30_0369"} />
            <FootnoteRef refNumber={31} ref={"https://sp17.datastructur.es/materials/lectures/lec37/lec37.html"} />
            <FootnoteRef refNumber={32} ref={"https://burntsushi.net/transducers"} />
            <FootnoteRef refNumber={33} ref={"https://dl.acm.org/doi/pdf/10.1145/318898.318923"} />
            <FootnoteRef refNumber={34} ref={"https://web.stanford.edu/class/cs345d-01/rl/cstore.pdf"} />
            <FootnoteRef refNumber={35} ref={"https://www.cs.umd.edu/~abadi/papers/abadi-sigmod08.pdf"} />
            <FootnoteRef refNumber={36} ref={"https://arxiv.org/abs/1207.2189"} />
            <FootnoteRef refNumber={37} ref={"https://stackoverflow.com/questions/69477460/is-rust-trait-the-same-as-java-interface/69485860#69485860"} />
            <FootnoteRef refNumber={38} ref={"https://dev.mysql.com/doc/refman/8.4/en/innodb-file-space.html"} />
          </ol>
        </section>
      </div>
    </div>
  );
}
